
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, CalendarRange, Tag } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import CountrySelector from "@/components/country/CountrySelector";
import DateRangeFilter from './DateRangeFilter';

interface ContentSearchProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  activeTagFilter: string | null;
  setActiveTagFilter: (tag: string | null) => void;
  latestTags: string[];
  allTags: string[];
  fromDate: Date | undefined;
  toDate: Date | undefined;
  setFromDate: (date: Date | undefined) => void;
  setToDate: (date: Date | undefined) => void;
}

const ContentSearch = ({ 
  searchTerm, 
  setSearchTerm, 
  activeFilter, 
  setActiveFilter,
  activeTagFilter,
  setActiveTagFilter,
  latestTags,
  allTags,
  fromDate,
  toDate,
  setFromDate,
  setToDate
}: ContentSearchProps) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [openTagSearch, setOpenTagSearch] = useState(false);

  const contentTypes = [
    { id: "all", label: "All" },
    { id: "podcast", label: "Podcasts" },
    { id: "video", label: "Videos" },
    { id: "summary", label: "Summaries" }
  ];

  const clearDateFilters = () => {
    setFromDate(undefined);
    setToDate(undefined);
  };

  return (
    <div className="max-w-4xl mx-auto mb-10">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-6">
          <div className="relative flex-1 mr-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input 
              placeholder="Search for topics, issues, or keywords..." 
              className="pl-10"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <CountrySelector />
        </div>
        
        {/* Content Type Filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Filter size={16} />
            Content Type:
          </span>
          <div className="flex flex-wrap gap-2">
            {contentTypes.map(type => (
              <Button 
                key={type.id}
                variant={activeFilter === type.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveFilter(type.id)}
                className={activeFilter === type.id ? "bg-nation-purple hover:bg-nation-purple-dark" : ""}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Tags Filter */}
        {latestTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            <div className="w-full flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Tag size={16} />
                Latest Tags:
              </span>
              <Popover open={openTagSearch} onOpenChange={setOpenTagSearch}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    Search All Tags
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0">
                  <Command>
                    <CommandInput placeholder="Search tags..." />
                    <CommandList>
                      <CommandEmpty>No tags found.</CommandEmpty>
                      <CommandGroup>
                        {allTags.map(tag => (
                          <CommandItem
                            key={tag}
                            onSelect={() => {
                              setActiveTagFilter(activeTagFilter === tag ? null : tag);
                              setOpenTagSearch(false);
                            }}
                          >
                            <span className={activeTagFilter === tag ? "font-bold" : ""}>
                              {tag}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-wrap gap-2">
              {latestTags.map(tag => (
                <Button 
                  key={tag}
                  variant={activeTagFilter === tag ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveTagFilter(activeTagFilter === tag ? null : tag)}
                  className={activeTagFilter === tag ? "bg-nation-purple hover:bg-nation-purple-dark" : ""}
                >
                  {tag}
                </Button>
              ))}
            </div>
          </div>
        )}
        
        {/* Advanced Filters */}
        <Collapsible 
          open={showAdvancedFilters} 
          onOpenChange={setShowAdvancedFilters}
          className="mt-4"
        >
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1 text-nation-purple">
                <Filter size={16} />
                {showAdvancedFilters ? "Hide advanced filters" : "Show advanced filters"}
              </Button>
            </CollapsibleTrigger>

            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Sort by:</span>
              <Button variant="ghost" size="sm" className="text-gray-600">
                Newest
              </Button>
              <Button variant="ghost" size="sm" className="text-gray-600">
                Most Viewed
              </Button>
            </div>
          </div>
          
          <CollapsibleContent className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <CalendarRange size={16} className="text-nation-purple" />
                <span className="text-sm font-medium text-gray-700">Date Range:</span>
              </div>
              
              <DateRangeFilter 
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={setFromDate}
                onToDateChange={setToDate}
                onClearDates={clearDateFilters}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
};

export default ContentSearch;
